import {createContext, ReactNode, useContext, useEffect, useRef, useState} from "react";
import firebase from "firebase/compat/app";
import 'firebase/compat/auth'
import {APIurl} from "./Utils";
import {Subscription, UserDataModel} from "./Model";
import {FeedbackContext} from "./FeedbackContext";


export const AuthContext = createContext<any>(null);

interface AuthProviderProps {
    children?: ReactNode
}

export enum AUTHSTATES {
    UNKNOWN,
    LOGGEDIN,
    LOGGEDOUT,
}

export function AuthProvider(props: AuthProviderProps) {

    const {setError} = useContext(FeedbackContext);

    const [authState, setAuthState] = useState<AUTHSTATES>(AUTHSTATES.UNKNOWN);
    const [currentUser, setCurrentUser] = useState<any>(null);
    const [userData, setUserData] = useState<any>(null);
    const [subscriptions, setSubscriptions] = useState<Subscription[]>([]);

    // SSO da Flutter: `?login_code=...` nell'URL (impostato da
    // subscription_sheet.dart/profile_screen.dart prima di aprire il
    // portale — vedi POST /auth/createWebLoginCode). Letto una sola volta,
    // in modo sincrono, prima che qualunque redirect possa scattare
    // (Router.tsx monta <BrowserRouter> solo quando authState non è più
    // UNKNOWN, quindi finché non risolviamo lo scambio qui sotto l'utente
    // vede solo lo spinner iniziale, mai un redirect a /login).
    const [loginCode] = useState(() => new URLSearchParams(window.location.search).get("login_code"));
    const [exchangingLoginCode, setExchangingLoginCode] = useState(!!loginCode);
    const exchangingRef = useRef(exchangingLoginCode);
    exchangingRef.current = exchangingLoginCode;

    useEffect(() => {
        if (loginCode) {
            // Il login_code vince sempre, anche se questo browser ha già
            // una sessione persistita per un altro utente — chi ha toccato
            // "Abbonati ora" nell'app l'ha fatto per QUEL account.
            exchangeLoginCode(loginCode);
        }

        const unsubscribe = firebase.auth().onAuthStateChanged((user) => {
            // Mentre lo scambio del login_code è in corso, l'esito dello
            // scambio (exchangeLoginCode, sotto) è l'unica cosa che decide
            // authState — qualunque evento onAuthStateChanged intermedio
            // (es. la sessione persistita prima ancora del custom-token
            // sign-in) viene ignorato per evitare di risolvere sull'utente
            // sbagliato.
            if (exchangingRef.current) return;

            if (user) {
                resolveUser(user);
            } else {
                setUserData(null)
                setCurrentUser(null)
                setAuthState(AUTHSTATES.LOGGEDOUT)
            }
        })
        return unsubscribe;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    function resolveUser(user: firebase.User) {
        fetchUserData(user.uid)
            .then(res => {
                setUserData(res.user)
                setSubscriptions(res.subscriptions.data as Subscription[])
                setCurrentUser(user)
                setAuthState(AUTHSTATES.LOGGEDIN)
            })
            .catch(() => {
                // Primo accesso Google/Apple: Firebase crea l'utente lato
                // client, quindi la riga MySQL (+ Customer Stripe) non
                // esiste ancora finché POST /registerSocialUser non la
                // crea — a differenza di email/password (POST /register,
                // già server-side). GET /getUser 404 in quel caso, non per
                // una sessione davvero invalida: un tentativo di recovery
                // prima di arrendersi e fare signOut.
                ensureBackendUser(user)
                    .then(() => fetchUserData(user.uid))
                    .then(res => {
                        setUserData(res.user)
                        setSubscriptions(res.subscriptions.data as Subscription[])
                        setCurrentUser(user)
                        setAuthState(AUTHSTATES.LOGGEDIN)
                    })
                    .catch(() => {
                        firebase.auth().signOut().then(() => {
                            setUserData(null)
                            setCurrentUser(null)
                            setAuthState(AUTHSTATES.LOGGEDOUT)
                        })
                    })
            })
    }

    async function ensureBackendUser(user: firebase.User) {
        const token = await user.getIdToken();
        const [name, surname] = splitName(user.displayName);
        await fetch(`${APIurl}/registerSocialUser`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${token}`
            },
            body: JSON.stringify({name, surname}),
        });
    }

    function splitName(displayName: string | null): [string | undefined, string | undefined] {
        const trimmed = displayName?.trim() ?? "";
        if (!trimmed) return [undefined, undefined];
        const parts = trimmed.split(/\s+/);
        if (parts.length === 1) return [parts[0], undefined];
        return [parts[0], parts.slice(1).join(" ")];
    }

    async function exchangeLoginCode(code: string) {
        stripLoginCodeFromUrl();
        try {
            const response = await fetch(`${APIurl}/auth/exchangeWebLoginCode`, {
                method: "POST",
                headers: {"Content-Type": "application/json"},
                body: JSON.stringify({code}),
            });
            if (!response.ok) {
                throw new Error("Invalid or expired login link");
            }
            const {customToken} = await response.json();
            await firebase.auth().signInWithCustomToken(customToken);

            setExchangingLoginCode(false);
            const user = firebase.auth().currentUser;
            if (user) {
                resolveUser(user);
            } else {
                setUserData(null)
                setCurrentUser(null)
                setAuthState(AUTHSTATES.LOGGEDOUT)
            }
        } catch (e: unknown) {
            setError(e);
            setExchangingLoginCode(false);
            setUserData(null)
            setCurrentUser(null)
            setAuthState(AUTHSTATES.LOGGEDOUT)
        }
    }

    function stripLoginCodeFromUrl() {
        const url = new URL(window.location.href);
        url.searchParams.delete("login_code");
        window.history.replaceState(null, "", url.toString());
    }

    // Ri-carica i dati utente (profilo + abbonamenti) senza un
    // window.location.reload() completo — usato dopo disdetta/riattivazione
    // abbonamento per aggiornare la UI restando sulla pagina.
    async function refreshUserData() {
        const user = firebase.auth().currentUser;
        if (!user) return;
        const res = await fetchUserData(user.uid);
        setUserData(res.user)
        setSubscriptions(res.subscriptions.data as Subscription[])
    }

    async function fetchUserData(uid: string): Promise<{ user: UserDataModel, subscriptions: {data: Subscription[]} }> {
        try {
            const user = firebase.auth().currentUser;
            if (!user) {
                throw new Error(`User not authenticated`);
            }

            const token = await user.getIdToken();

            const response = await fetch(`${APIurl}/getUser/${uid}`, {
                method: "GET",
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${token}`
                }
            })
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`)
            }
            return await response.json();

        } catch (error) {
            throw error;
        }
    }

    return (
        <AuthContext.Provider value={{currentUser, authState, userData, subscriptions, refreshUserData}}>
            {props.children}
        </AuthContext.Provider>
    )
}
